import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { AppConfig } from "../AppConfig";
import { NFSStatus } from "./nfsUtils";
import { toast } from "react-toastify";
import { GetCodedValueAndAlias, getMapView, projectToLatLong } from "./arcgisUtils";
import { getAGEToken } from "./authenticate";
import Graphic from "@arcgis/core/Graphic";
import { formatDate } from "./helpers";
import { getSeaMapNumber } from "./autofillUtils";
import type Point from "@arcgis/core/geometry/Point";

export enum EFSType {
    P = "P",
    T = "T",
}

export enum EFSObjectStatus {
    // NEW = "NEW",
    // CHANGE = "CHANGE",
    // DISCONTINUED = "DISCONTINUED",
    // REMOVED = "REMOVED",
    IALA = "IALA-OMSKJERMINGER",
    ESTABLISHMENT = "ETABLERINGER",
    CHARACTERCHANGE = "KARAKTER, ENDRINGER",
    SHUTDOWNS = "NEDLEGGELSER, FJERNINGER",
    GONEDARK = "SLUKKET",
    WRECKED = "HAVARERT, BORTE",
    RACON = "RACON",
}

export interface EFSObject {
    nfsObject: Graphic;
    efsType: EFSType;
    efsSeaMapId?: string; // Optional, can be undefined if not set
    efsLat?: number; // Latitude of the EFS object
    efsLon?: number; // Longitude of the EFS object
    efsMessage: string;
    efsFromDate?: Date; // Optional, can be undefined if not set
    efsToDate?: Date; // Optional, can be undefined if not set
    objectStatus: EFSObjectStatus;
    efsTemplate?: Graphic; // Optional, can be undefined if not set
    efsMessageToSend?: string; // Optional, can be used for additional information
}

export const getEfsOptions = async () => {
    const efsTemplateLayer = new FeatureLayer({
        url: AppConfig.EFSTemplateLayerUrl,
        outFields: ["*"],
        title: "EFS Template Layer",
        id: "EFS Template Layer",
    });

    try {
        const results = await efsTemplateLayer.queryFeatures({
            where: "1=1",
            returnGeometry: false,
            outFields: ["*"],
        });

        if (results.features.length > 0) {
            return results.features;
        }
    } catch (error) {
        console.error("Error fetching EFS templates:", error);
        toast.error("Noe gikk galt under henting av EFS maler");
    }
    return [];
};

export const getPropsedValuesFromFeature = (feature: Graphic, efsTemplates: Graphic[]) => {
    const propsedValues: Record<string, any> = {};

    const efsAttributes = feature.attributes;

    switch (efsAttributes.nfs_status) {
        case NFSStatus.DISCONTINUED:
        case NFSStatus.DISCONTINUED_CHANGED:
        case NFSStatus.PROJECTED_DISCONTINUED:
            propsedValues.objectStatus = EFSObjectStatus.SHUTDOWNS;
            propsedValues.efsType = EFSType.P;
            break;
        case NFSStatus.REMOVED:
        case NFSStatus.REMOVED_CHANGED:
        case NFSStatus.PROJECTED_REMOVED:
            propsedValues.objectStatus = EFSObjectStatus.SHUTDOWNS;
            propsedValues.efsType = EFSType.P;
            break;
        case NFSStatus.OPERATIVE:
            propsedValues.objectStatus = EFSObjectStatus.ESTABLISHMENT;
            propsedValues.efsType = EFSType.P;
            break;
        case NFSStatus.PROJECTED:
        case NFSStatus.PROJECTED_EXISTING:
        case NFSStatus.PARTIALLY_COMPLETED:
        case NFSStatus.DEPLOYED_CHANGED:
        case NFSStatus.INITIATED:
            propsedValues.objectStatus = EFSObjectStatus.ESTABLISHMENT;
            propsedValues.efsType = EFSType.P;
            break;
        case NFSStatus.UNDEFINED:
    }

    let relevantTemplates = [...efsTemplates];

    if (feature.layer?.type === "feature") {
        const nfsLayer = feature.layer as FeatureLayer;
        relevantTemplates = relevantTemplates.filter(
            (template) => template.attributes.nautisk_objekt_kilde === nfsLayer.layerId
        );
    }

    if (relevantTemplates.length > 0) {
        propsedValues.efsTemplate = relevantTemplates[0];
        propsedValues.efsMessage = relevantTemplates[0].attributes.melding_norsk;
    }

    if (Object.hasOwn(feature.attributes, "klasse")) {
        relevantTemplates = relevantTemplates.filter((template) => {
            switch (template.attributes.nautisk_objekt_kilde) {
                case 0:
                    return template.attributes.lysklasse === feature.attributes.klasse;
                case 4:
                    return template.attributes.raconklasse === feature.attributes.klasse;
                case 5:
                    return template.attributes.fastklasse === feature.attributes.klasse;
                case 6:
                    return template.attributes.flytendeklasse === feature.attributes.klasse;
                case 7:
                    return template.attributes.overettklasse === feature.attributes.klasse;
                case 8:
                    return template.attributes.overettklasse === feature.attributes.klasse;
            }
        });
    }

    if (relevantTemplates.length > 0) {
        propsedValues.efsTemplate = relevantTemplates[0];
        propsedValues.efsMessage = relevantTemplates[0].attributes.melding_norsk;
    }

    return propsedValues;
};

export const updateNauticalObjectsFromEFSObjects = (efsObjects: EFSObject[]) => {
    console.log(efsObjects);
};

const getEfsEnabledLayers = (): FeatureLayer[] => {
    const mapview = getMapView();
    if (!mapview?.map) return [];

    return mapview.map.layers
        .filter((layer) => layer instanceof FeatureLayer && AppConfig.EFSLayerNames.includes(layer.id))
        .toArray() as FeatureLayer[];
};

export const getExpiredEFS = async () => {
    const allExpiredEfs: { [layerName: string]: Graphic[] } = {};
    const relevantLayers = getEfsEnabledLayers();

    // Create an array of promises for parallel execution
    const queryPromises = relevantLayers.map(async (featureLayer) => {
        try {
            const expiredEfs = await featureLayer.queryFeatures({
                outFields: ["*"],
                where: "gyldig_til < CURRENT_TIMESTAMP",
            });

            if (expiredEfs.features.length > 0) {
                return { layerName: featureLayer.id, features: expiredEfs.features };
            }
        } catch (error) {
            console.error(`Error querying EFS layer ${featureLayer.id}:`, error);
        }

        return null;
    });

    // Execute all queries in parallel
    const results = await Promise.all(queryPromises);

    // Collect results from successful queries
    results.forEach((result) => {
        if (result && result.features && result.features.length > 0) {
            allExpiredEfs[result.layerName] = result.features;
        }
    });

    return allExpiredEfs;
};

export const getActiveEFS = async () => {
    const allActiveEfs: { [layerName: string]: Graphic[] } = {};

    const relevantLayers = getEfsEnabledLayers();

    // Create an array of promises for parallel execution
    const queryPromises = relevantLayers.map(async (featureLayer) => {
        try {
            const activeEfs = await featureLayer.queryFeatures({
                outFields: ["*"],
                where: "gyldig_til >= CURRENT_TIMESTAMP",
            });

            if (activeEfs.features.length > 0) {
                return { layerName: featureLayer.id, features: activeEfs.features };
            }
        } catch (error) {
            console.error(`Error querying EFS layer ${featureLayer.id}:`, error);
        }

        return null;
    });

    // Execute all queries in parallel
    const results = await Promise.all(queryPromises);

    // Collect results from successful queries
    results.forEach((result) => {
        if (result && result.features && result.features.length > 0) {
            allActiveEfs[result.layerName] = result.features;
        }
    });

    return allActiveEfs;
};

export const getAllObjectsWithEFS = async () => {
    const allEfs: { [layerName: string]: Graphic[] } = {};

    const relevantLayers = getEfsEnabledLayers();

    // Create an array of promises for parallel execution
    const queryPromises = relevantLayers.map(async (featureLayer) => {
        try {
            const activeEfs = await featureLayer.queryFeatures({
                outFields: ["*"],
                where: "efs_melding IS NOT NULL OR gyldig_fra IS NOT NULL OR gyldig_til IS NOT NULL",
            });

            if (activeEfs.features.length > 0) {
                return { layerName: featureLayer.id, features: activeEfs.features };
            }
        } catch (error) {
            console.error(`Error querying EFS layer ${featureLayer.id}:`, error);
        }

        return null;
    });

    // Execute all queries in parallel
    const results = await Promise.all(queryPromises);

    // Collect results from successful queries
    results.forEach((result) => {
        if (result && result.features && result.features.length > 0) {
            allEfs[result.layerName] = result.features;
        }
    });

    return allEfs;
};
/**
 * Helper function to generate a formatted string for a single EFS object
 * @param efsObject The EFS object to format
 * @returns A formatted string representing the EFS object
 */
export const generateStringFromEFSObject = (efsObject: EFSObject): string => {
    // Extract relevant data from the EFS object
    const nfsObject = efsObject.nfsObject;
    const attributes = nfsObject.attributes ?? {};

    // Extract object ID or use a placeholder
    const seaMarkNr = attributes.sjomerke_nr ?? "Ukjent ID";

    const name = attributes.navn ?? "Ukjent navn";

    // Get the sea map ID
    const seaMapId = efsObject.efsSeaMapId ?? "";

    // Get latitude and longitude with proper formatting
    const latitude = efsObject.efsLat ? efsObject.efsLat.toFixed(6) : "";
    const longitude = efsObject.efsLon ? efsObject.efsLon.toFixed(6) : "";

    let validFrom = "";
    let validTo = "";
    if (efsObject.efsFromDate) {
        validFrom = formatDate(efsObject.efsFromDate.getTime());
    }
    if (efsObject.efsToDate) {
        validTo = formatDate(efsObject.efsToDate.getTime());
    }

    // Get the class information
    let classInfo: string | number = "";
    if (Object.hasOwn(attributes, "klasse")) {
        if (efsObject.nfsObject.layer) {
            classInfo = GetCodedValueAndAlias(efsObject.nfsObject.layer, "klasse", attributes.klasse)[1] ?? "";
        }
    }

    let karakter = "";
    if (Object.hasOwn(attributes, "lys_karakter")) {
        karakter = attributes.lys_karakter ?? "";
        if (karakter !== "") {
            karakter = GetCodedValueAndAlias(efsObject.nfsObject.layer, "lys_karakter", karakter)[1].toString() ?? "";
        }
    } else if (Object.hasOwn(attributes, "karakter")) {
        karakter = attributes.karakter ?? "";
        if (karakter !== "") {
            karakter = GetCodedValueAndAlias(efsObject.nfsObject.layer, "karakter", karakter)[1].toString() ?? "";
        }
    }

    let messageTemplate = (efsObject.efsTemplate?.attributes.melding_norsk ?? "") as string;
    if (messageTemplate.toLowerCase().includes("karakter: ###")) {
        messageTemplate = messageTemplate.replace("arakter: ###", `arakter: ${karakter ?? ""}`);
    }
    if (messageTemplate.toLowerCase().includes("varighet: ###")) {
        messageTemplate = messageTemplate.replace("arighet: ###", `arighet: ${validFrom} - ${validTo}`);
    }

    let changeType = efsObject.efsTemplate?.attributes.endring_type ?? "";
    // Get the change type based on objectStatus
    if (!changeType || changeType === "") {
        switch (efsObject.objectStatus) {
            case EFSObjectStatus.ESTABLISHMENT:
                changeType = "Etableres";
                break;
            case EFSObjectStatus.CHARACTERCHANGE:
                changeType = "Endres";
                break;
            case EFSObjectStatus.SHUTDOWNS:
                changeType = "Nedlegges";
                break;
            case EFSObjectStatus.GONEDARK:
                changeType = "Slukket";
                break;
            case EFSObjectStatus.WRECKED:
                changeType = "Havarert";
                break;
            case EFSObjectStatus.RACON:
                changeType = "Racon";
                break;
            case EFSObjectStatus.IALA:
                changeType = "IALA-omskjerming";
                break;
        }
    }
    // Format the string according to the example: "569000, Fyrlykt, Melsteinen, 51, 65.187544, 11.849787, Fyrlykt, Retableres"
    return `(${seaMarkNr}, ${classInfo}, ${name}), ${seaMapId}, ${latitude}, ${longitude}, ${classInfo}, ${changeType}, ${validFrom}, ${validTo}, ${messageTemplate}`.trim();
};

/**
 * Generates a text file content with one line per EFS object
 * @param efsObjects Array of EFS objects to include in the text file
 * @returns A string containing the formatted text file content
 */
export const generateTxtFileFromEFSObjects = (efsObjects: EFSObject[], senderName: string): string => {
    const information = `EFS-melding generert av Nautisk Fagsystem (NFS) for avsender ${senderName}.
Header er veiledende for strukturen på følgende data.
Varianter av meldinger kan forekomme.
    `;
    // Define the header line
    const header =
        "Utstyrsbeskrivelse, Sjøkart, Bredde, Lengde, Klasse, Endringstype, gyldig fra, gyldig til, Meldingstekst";

    // Generate a line for each EFS object using the helper function
    const lines = efsObjects.map((obj) => obj.efsMessageToSend);

    // Combine the header and all lines with newline characters
    return [information, header, ...lines].join("\n");
};

/**
 * Generates a text file from EFS objects and triggers a download in the browser
 * @param efsObjects Array of EFS objects to include in the text file
 * @param filename Optional filename for the downloaded file (default: "efs-objects.txt")
 */
export const downloadEFSObjectsAsTxt = (efsObjects: EFSObject[], senderName: string): void => {
    // Generate the text content
    const content = generateTxtFileFromEFSObjects(efsObjects, senderName);

    const filename = "efs-melding-" + new Date().toISOString() + ".txt";

    // Create a Blob with the content
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor element
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    // Append to the document, trigger click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up by revoking the Blob URL
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 100);
};

export const uploadEFSMessageToServer = async (efsObjects: EFSObject[], nameOfSender: string): Promise<void> => {
    try {
        const content = generateTxtFileFromEFSObjects(efsObjects, nameOfSender);
        const filename = "efs-melding-" + new Date().toISOString() + ".txt";

        // Create a Blob with the content
        const blob = new Blob([content], { type: "text/plain;charset=utf-8" });

        // Convert Blob to File object
        const file = new File([blob], filename, { type: "text/plain;charset=utf-8" });

        // Create a FormData object to send the file
        const formData = new FormData();
        formData.append("file", file);

        const token = await getAGEToken();

        const url = `${AppConfig.NFSAPI.BaseApiUrl}sksd/uploadEFSMessage`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                // Don't manually set Content-Type for FormData with files
                // Browser will set it automatically with the correct boundary
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        // Handle different response statuses as per the Swagger spec
        if (!response.ok) {
            const errorText = await response.text();

            if (response.status === 400) {
                throw new Error(`Bad Request: ${errorText}`);
            } else if (response.status === 500) {
                throw new Error(`Internal Server Error: ${errorText}`);
            } else {
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
        }

        const data = await response.json();
        toast.success("EFS-melding lastet opp til serveren");
        return data;
    } catch (error) {
        console.error("Error uploading EFS message:", error);
        toast.error(`Feil under opplasting av EFS-melding: ${error instanceof Error ? error.message : "Ukjent feil"}`);
        throw error;
    }
};

export const updateNauticalObjectsWithEfsData = async (
    efsObjects: EFSObject[],
    senderName: string
): Promise<boolean> => {
    let success = true;

    for (const efsObject of efsObjects) {
        const layer = efsObject.nfsObject.layer as FeatureLayer;
        if (!layer) {
            console.error("Nautical object layer is not defined for EFS object:", efsObject);
            success = false;
            continue;
        }
        const attributesToUpdate = new Graphic({
            attributes: {
                objectid: efsObject.nfsObject.attributes.objectid,
                efs_melding: efsObject.efsType + "-melding - " + efsObject.efsMessageToSend,
                gyldig_fra: efsObject.efsFromDate?.getTime() ?? null,
                gyldig_til: efsObject.efsToDate?.getTime() ?? null,
                efs_sendt_av: senderName,
            },
        });
        await layer.applyEdits({ updateFeatures: [attributesToUpdate] });
    }

    return success;
};

const generateCompletionTxtFileFromEFSObjects = (
    efsObjects: { nfsObject: Graphic; efsMessageToSend: string }[],
    senderName: string
): string => {
    const information = `EFS-melding generert av Nautisk Fagsystem (NFS) for avsender ${senderName}.
        `;
    const efsMessages = efsObjects
        .map((efsObject) => {
            return efsObject.efsMessageToSend;
        })
        .join("\n");

    return `${information}
${efsMessages}
    `;
};

export const sendEFSCancelMessage = async (
    efsObjects: { nfsObject: Graphic; efsMessageToSend: string }[],
    senderName: string
): Promise<boolean> => {
    try {
        const content = generateCompletionTxtFileFromEFSObjects(efsObjects, senderName);
        const filename = "efs-melding-" + new Date().toISOString() + "_avslutning.txt";

        // Create a Blob with the content
        const blob = new Blob([content], { type: "text/plain;charset=utf-8" });

        // Convert Blob to File object
        const file = new File([blob], filename, { type: "text/plain;charset=utf-8" });

        // Create a FormData object to send the file
        const formData = new FormData();
        formData.append("file", file);

        const token = await getAGEToken();

        const url = `${AppConfig.NFSAPI.BaseApiUrl}sksd/uploadEFSMessage`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                // Don't manually set Content-Type for FormData with files
                // Browser will set it automatically with the correct boundary
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        // Handle different response statuses as per the Swagger spec
        if (!response.ok) {
            const errorText = await response.text();

            if (response.status === 400) {
                throw new Error(`Bad Request: ${errorText}`);
            } else if (response.status === 500) {
                throw new Error(`Internal Server Error: ${errorText}`);
            } else {
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
        }

        const data = await response.json();
        await removeEFSFromNauticalObjects(efsObjects);
        toast.success("EFS-melding lastet opp til serveren");
        return data;
    } catch (error) {
        console.error("Error uploading EFS message:", error);
        toast.error(`Feil under opplasting av EFS-melding: ${error instanceof Error ? error.message : "Ukjent feil"}`);
        throw error;
    }
};

const removeEFSFromNauticalObjects = async (
    efsObjects: { nfsObject: Graphic; efsMessageToSend: string }[]
): Promise<boolean> => {
    let success = true;

    for (const efsObject of efsObjects) {
        const layer = efsObject.nfsObject.layer as FeatureLayer;
        if (!layer) {
            console.error("Nautical object layer is not defined for EFS object:", efsObject);
            success = false;
            continue;
        }
        const attributesToUpdate = new Graphic({
            attributes: {
                objectid: efsObject.nfsObject.attributes.objectid,
                efs_melding: null,
                gyldig_fra: null,
                gyldig_til: null,
                efs_sendt_av: null,
            },
        });
        await layer.applyEdits({ updateFeatures: [attributesToUpdate] });
    }

    return success;
};

/**
 * Formats a decimal degree value the way EFS messages expect it,
 * e.g. 67.341099 -> "67,341099" and 13.562978 -> "013,562978"
 */
const formatEFSCoordinate = (value: number, integerDigits: number): string => {
    const [integerPart, decimalPart = ""] = Math.abs(value).toFixed(6).split(".");
    return `${integerPart.padStart(integerDigits, "0")},${decimalPart}`;
};

/**
 * Builds a human readable description of a nautical object,
 * e.g. "700510, Lysbøye, Vestfjorden Waverider Tennholmen"
 */
const describeNauticalObject = (nauticalObject: Graphic): string => {
    const attributes = nauticalObject.attributes ?? {};
    const seaMarkNr = attributes.sjomerke_nr ?? "Ukjent sjømerkenummer";
    const name = attributes.navn ?? "Ukjent navn";

    let classInfo = "";
    if (Object.hasOwn(attributes, "klasse") && nauticalObject.layer) {
        classInfo = GetCodedValueAndAlias(nauticalObject.layer, "klasse", attributes.klasse)[1]?.toString() ?? "";
    }

    return [seaMarkNr, classInfo, name].filter((part) => part !== "").join(", ");
};

/**
 * Builds the sea map clause for a nautical object, e.g. ", sjøkart: 21, 22". The numbers are
 * looked up from the chart index layers the object is positioned in. Returns an empty string
 * when the object has no geometry or the lookup fails, so a missing chart number never blocks
 * the message.
 */
const getSeaMapClause = async (nauticalObject: Graphic): Promise<string> => {
    if (!nauticalObject.geometry) return "";

    try {
        const seaMapNumbers = await getSeaMapNumber(nauticalObject.geometry as Point);
        return seaMapNumbers === "" ? "" : `, sjøkart: ${seaMapNumbers}`;
    } catch (error) {
        console.error("Error looking up sea map number for EFS message:", error);
        return "";
    }
};

/**
 * Generates the EFS message text for an object that has been removed or discontinued,
 * optionally mentioning the object that has been established as a replacement.
 * @param removedOrDiscontinuedObject The nautical object that has been removed or discontinued
 * @param replacementObject Optional nautical object established as a replacement
 * @returns The message text to send by email
 */
export const generateEFSMessageForRemovedOrDiscontinuedObject = async (
    removedOrDiscontinuedObject: Graphic,
    replacementObject?: Graphic
): Promise<string> => {
    // Template for the EFS message
    // Hei.
    // Følgende objekt er fjernet:
    //   (or for discontinued: Følgende objekt er nedlagt som navigasjonsobjekt, men fysisk installasjon står fortsatt i felt:)
    // 700510, Lysbøye, Vestfjorden Waverider Tennholmen i posisjon: N 67,341099° E 013,562978°
    // Som erstatning er det opprettet en (klasse),(sjømerkenummer og navn)(hentes fra klasse sjømerkenummer og navn)  denne sendes via Inmat

    const status = removedOrDiscontinuedObject.attributes?.nfs_status;
    const isDiscontinued =
        status === NFSStatus.DISCONTINUED ||
        status === NFSStatus.DISCONTINUED_CHANGED ||
        status === NFSStatus.PROJECTED_DISCONTINUED;

    let position = "";
    if (removedOrDiscontinuedObject.geometry) {
        try {
            const latLongGeometry = (await projectToLatLong(removedOrDiscontinuedObject.geometry)) as Point;
            if (latLongGeometry?.latitude != null && latLongGeometry?.longitude != null) {
                const latitude = formatEFSCoordinate(latLongGeometry.latitude, 2);
                const longitude = formatEFSCoordinate(latLongGeometry.longitude, 3);
                const latitudeHemisphere = latLongGeometry.latitude < 0 ? "S" : "N";
                const longitudeHemisphere = latLongGeometry.longitude < 0 ? "W" : "E";
                position = ` i posisjon: ${latitudeHemisphere} ${latitude}° ${longitudeHemisphere} ${longitude}°`;
            }
        } catch (error) {
            console.error("Error projecting geometry for EFS message:", error);
        }
    }

    const seaMap = await getSeaMapClause(removedOrDiscontinuedObject);

    const lines = [
        "Hei.",
        isDiscontinued
            ? "Følgende objekt er nedlagt som navigasjonsobjekt, men fysisk installasjon står fortsatt i felt:"
            : "Følgende objekt er fjernet:",
        `${describeNauticalObject(removedOrDiscontinuedObject)}${position}${seaMap}`,
    ];

    if (replacementObject) {
        const replacementSeaMap = await getSeaMapClause(replacementObject);
        lines.push(
            `Som erstatning er det opprettet ${describeNauticalObject(
                replacementObject
            )}${replacementSeaMap}, denne sendes via Inmat`
        );
    }

    return lines.join("\n");
};
